import React from 'react';
import styled from '@emotion/styled';
import { Link } from 'react-router-dom';
import { BlogPost } from '../../../../data/blogPosts';
import { Calendar, Clock, ArrowRight } from 'lucide-react';

const Card = styled(Link)`
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  overflow: hidden;
  text-decoration: none;
  transition: all 0.2s ease;
  height: 100%;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.05);
    border-color: #d1d5db;
  }
`;

const ImageContainer = styled.div`
  width: 100%;
  height: 200px;
  background: #f3f4f6;
  overflow: hidden;
  position: relative;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }

  &:hover img {
    transform: scale(1.05);
  }
`;



const Content = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const Meta = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 12px;
  font-size: 13px;
  color: #6b7280;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const Category = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #7c3aed;
  background: #ede9fe;
  padding: 4px 10px;
  border-radius: 100px;
  margin-bottom: 16px;
  align-self: flex-start;
`;

const Title = styled.h3`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 12px;
  line-height: 1.4;
`;

const Description = styled.p`
  font-size: 15px;
  color: #4b5563;
  line-height: 1.6;
  margin-bottom: 24px;
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: auto;
  padding-top: 16px;
  border-top: 1px solid #f3f4f6;
`;

const Author = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
`;

const ReadMore = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  font-weight: 600;
  color: #7c3aed;
`;

interface BlogCardProps {
  post: BlogPost;
}

const BlogCard: React.FC<BlogCardProps> = ({ post }) => {
  return (
    <Card to={`/blog/${post.slug}`}>
      <ImageContainer>
        {post.imageUrl ? (
          <img src={post.imageUrl} alt={post.title} onError={(e) => {
            (e.target as HTMLImageElement).src = 'https://placehold.co/600x400/f3f4f6/a78bfa?text=SmushChat+Blog';
          }} />
        ) : (
          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>
            No Image
          </div>
        )}
      </ImageContainer>
      <Content>
        <Category>{post.category}</Category>
        <Title>{post.title}</Title>
        <Description>{post.description}</Description>
        <Meta>
          <MetaItem>
            <Calendar size={14} />
            {new Date(post.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </MetaItem>
          <MetaItem>
            <Clock size={14} />
            {post.readTime}
          </MetaItem>
        </Meta>
        <Footer>
          <Author>{post.author}</Author>
          <ReadMore>
            Read Article <ArrowRight size={16} />
          </ReadMore>
        </Footer>
      </Content>
    </Card>
  );
};

export default BlogCard;
